import React, { FC } from 'react';
import { Marker, Popup, Circle } from 'react-leaflet';
import { Driver } from '../../types/driver.types';
import { Location } from '../../types/location.types';
import { truckIcon, selectedTruckIcon } from '../../utils/mapIcons';

interface TruckMarkerProps {
  driver: Driver;
  location: Location;
  isSelected: boolean;
  onClick?: () => void;
}

const TruckMarker: FC<TruckMarkerProps> = ({
  driver,
  location,
  isSelected,
  onClick
}) => {
  const formatLastUpdate = (lastUpdate: string) => {
    try {
      const updateTime = new Date(lastUpdate);
      const now = new Date();
      const diffMinutes = Math.floor((now.getTime() - updateTime.getTime()) / (1000 * 60));
      
      if (diffMinutes < 1) return 'Just now';
      if (diffMinutes < 60) return `${diffMinutes}m ago`;
      if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
      return updateTime.toLocaleDateString();
    } catch {
      return 'Unknown';
    }
  };

  const getStatusColor = (status: string, appState?: string) => {
    if (status === 'active') {
      if (appState?.includes('paused') || appState?.includes('background')) {
        return 'text-yellow-600';
      }
      return 'text-green-600';
    }
    return 'text-red-600';
  };

  const getStatusText = (status: string, appState?: string) => {
    if (status === 'active') {
      if (appState?.includes('paused') || appState?.includes('background')) {
        return 'Active (Background)';
      }
      return 'Active';
    }
    return 'Offline';
  };

  return (
    <>
      {/* Accuracy circle */}
      {location.accuracy && location.accuracy > 0 && (
        <Circle
          center={[location.lat, location.lng]}
          radius={location.accuracy}
          pathOptions={{
            color: isSelected ? '#ef4444' : '#3b82f6',
            fillColor: isSelected ? '#ef4444' : '#3b82f6',
            fillOpacity: 0.1,
            weight: 1
          }}
        />
      )}
      
      {/* Driver marker */}
      <Marker
        position={[location.lat, location.lng]}
        icon={isSelected ? selectedTruckIcon : truckIcon}
        eventHandlers={{
          click: onClick
        }}
      >
        <Popup maxWidth={300}>
          <div className="p-3 min-w-[250px]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-lg">{driver.name}</h3>
              <span className={`text-sm font-medium ${getStatusColor(location.status, location.appState)}`}>
                {getStatusText(location.status, location.appState)}
              </span>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="font-medium text-gray-600">Vehicle:</span>
                  <p>{driver.vehicleId}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Phone:</span>
                  <p>{driver.phone}</p>
                </div>
              </div>
              
              <hr className="my-2" />
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="font-medium text-gray-600">Speed:</span>
                  <p>{location.speed ? `${location.speed.toFixed(1)} km/h` : '0 km/h'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Accuracy:</span>
                  <p>{location.accuracy ? `±${location.accuracy.toFixed(0)}m` : 'N/A'}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="font-medium text-gray-600">Coordinates:</span>
                  <p className="text-xs font-mono">
                    {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
                  </p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Heading:</span>
                  <p>{location.heading ? `${location.heading.toFixed(0)}°` : 'N/A'}</p>
                </div>
              </div>
              
              <hr className="my-2" />
              
              <div>
                <span className="font-medium text-gray-600">Last Update:</span>
                <p className="text-xs">{formatLastUpdate(location.lastUpdate)}</p>
              </div>
              
              {location.appState && (
                <div>
                  <span className="font-medium text-gray-600">App State:</span>
                  <p className="text-xs capitalize">{location.appState.replace('AppLifecycleState.', '')}</p>
                </div>
              )}
            </div>
          </div>
        </Popup>
      </Marker>
    </>
  );
};

export default TruckMarker;